/**
 * 모임 발자취 — 여행이 끝난 뒤 어디를 어떻게 다녔는지.
 *
 * 기간은 서버가 모임 일정에서 만들어 붙입니다. 여기서 넘기지 않습니다 —
 * 기간 없는 조회는 meeting_trace 의 월별 파티션을 전부 훑습니다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapList } from 'common/utils/apiResponse';

/** 구간 하나 — 머문 곳, 또는 그 사이 이동 */
export interface TraceSegment {
    traceId: number;
    userNo: number;
    nickname?: string | null;
    dayNumber?: number | null;
    seq?: number | null;
    /** S=체류, M=이동 */
    kind: 'S' | 'M';
    placeName?: string | null;
    placeType?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    /** 일정과 맞은 경우. 없으면 "일정 밖" */
    subId?: number | null;
    startedAt?: string | null;
    endedAt?: string | null;
    durationSec?: number | null;
    distanceM?: number | null;
    /** W=걷기, B=자전거, C=차량, U=미상 */
    moveMode?: string | null;
}

export interface TraceDay {
    day: number | null;
    /** 체류만 — 지도의 핀과 타임라인의 줄이 됩니다 */
    stays: TraceSegment[];
    /** 체류 사이의 이동. stays[i] 다음 이동이 moves[i] 입니다 */
    moves: (TraceSegment | null)[];
    placeCount: number;
    distanceM: number;
    moveSec: number;
}

export const useMeetingTrace = (meetingId?: string | null, enabled = true) => {
    const [segments, setSegments] = useState<TraceSegment[]>([]);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = useCallback(async () => {
        if (!meetingId || !enabled) return;
        setLoading(true);
        try {
            const res = await PlaceReviewRepository.getMeetingTrace(meetingId);
            setSegments(unwrapList<TraceSegment>(res));
        } catch {
            /* 발자취가 없다고 모임 화면이 막히면 안 됩니다 */
            setSegments([]);
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    }, [meetingId, enabled]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await load();
        })();
        return () => { cancelled = true; };
    }, [load]);

    /**
     * 일차별로 묶고, 체류 사이에 이동을 끼웁니다.
     *
     * 서버는 시각순으로 주므로 "체류 → 이동 → 체류" 가 번갈아 옵니다.
     * 화면은 체류를 줄로 그리고 그 아래에 이동을 답니다.
     */
    const days = useMemo<TraceDay[]>(() => {
        const byDay = new Map<number | null, TraceSegment[]>();
        for (const s of segments) {
            const key = s.dayNumber ?? null;
            const list = byDay.get(key);
            if (list) list.push(s); else byDay.set(key, [s]);
        }

        return Array.from(byDay.entries())
            .sort((a, b) => {
                if (a[0] === null) return 1;
                if (b[0] === null) return -1;
                return a[0] - b[0];
            })
            .map(([day, list]) => {
                const ordered = [...list].sort(
                    (a, b) => String(a.startedAt).localeCompare(String(b.startedAt)));

                const stays: TraceSegment[] = [];
                const moves: (TraceSegment | null)[] = [];
                let pendingMove: TraceSegment | null = null;

                for (const s of ordered) {
                    if (s.kind === 'S') {
                        if (stays.length > 0) moves.push(pendingMove);
                        stays.push(s);
                        pendingMove = null;
                    } else {
                        pendingMove = s;
                    }
                }
                /* 마지막 체류 뒤에도 이동이 있으면 그대로 답니다 */
                if (stays.length > 0) moves.push(pendingMove);

                const distanceM = ordered.reduce((n, s) => n + (s.distanceM || 0), 0);
                const moveSec = ordered
                    .filter(s => s.kind === 'M')
                    .reduce((n, s) => n + (s.durationSec || 0), 0);

                return { day, stays, moves, placeCount: stays.length, distanceM, moveSec };
            });
    }, [segments]);

    return { segments, days, loading, loaded, reload: load };
};
